import React, {useEffect, useState} from 'react';
import SomlStatusService, {SomlStatusResponse} from '../../services/soml-status-service';
import {SomlStatusTask, StatusTaskOperation} from '../../types/Status';
import Loader from '../loader/Loader';
import {SlideDownTransition} from '../../common/Transitions';
import * as SomlUtils from '../../utils/soml-utils';
import {Configurations} from "../../types/Configurations";


import './StatusToolbar.scss';

// In milliseconds
const DEFAULT_POLL_PERIOD = 2000;
const UNKNOWN_OPERATION_LABEL_MAPPER = (operation: StatusTaskOperation) => `Unknown operation ${operation}`;

// TODO: THIS component needs to be notified in case of some action !

/**
 * Toolbar for visualizing background tasks status.
 */
export default function StatusToolbar(props: {
  pollPeriod?: number
  configurations: Configurations
}) {
  const [visible, setVisible] = useState(false);
  const [activeTasks, setActiveTasks] = useState<Array<any>>([]);
  const pollPeriod = props.pollPeriod || DEFAULT_POLL_PERIOD;
  const searchEnabled = props.configurations.endpoints.graphql.search || false;

  useEffect(() => {
    function fetchStatus() {

      const promises = [SomlStatusService.getSoaasSomlStatus('summary')];
      if (searchEnabled) {
        // Check if there is search graphql endpoint configured and try to bind the schema in the
        // search service as well.
        promises.push(SomlStatusService.getSearchSomlStatus('summary'));
      }
      Promise.allSettled(promises).then((results: Array<PromiseSettledResult<SomlStatusResponse>>) => {
        const tasks = readTasks(results);
        if (tasks.length > 0) {
          setActiveTasks(tasks);
          setVisible(true);
        } else {
          // Active tasks are kept to be visible when the toolbar is closing
          // Otherwise a bad flickering is observed
          setVisible(false);
        }

      }).catch((errors) => console.error("Unable to fetch service status!", errors));
    }

    fetchStatus();
    const pollerInterval = setInterval(() => fetchStatus(), pollPeriod);
    return () => clearInterval(pollerInterval);
  }, [pollPeriod, searchEnabled]);

  return (
    <SlideDownTransition in={visible}>
      <span className="StatusToolbar box-shadow-stronger">
        <Loader className="mr-1"/>
        {activeTasks}
      </span>
    </SlideDownTransition>
  );
}

// TODO: Not really sure how these should be rendered. We could render them as separate tasks Bind and Update..

function mapTasks(tasks: Array<SomlStatusTask>): Array<any> {
  return tasks.map((task) => {
    return <UpdateTask key={task.operation} task={task}/>
  });
}

function readTasks(results: Array<PromiseSettledResult<SomlStatusResponse>>) {
  const soaasResponse: PromiseSettledResult<SomlStatusResponse> = results[0];
  let soaasTasks = [];
  let searchTasks = [];
  if (soaasResponse.status === "fulfilled") {
    soaasTasks = mapTasks(soaasResponse.value.tasks || [])
  } else {
    console.error("Unable to fetch SOaaS status!", soaasResponse.reason)
  }
  if (results.length === 2) {
    const searchResponse: PromiseSettledResult<SomlStatusResponse> = results[1];
    if (searchResponse.status === "fulfilled") {
      searchTasks = mapTasks(searchResponse.value.tasks || [])
    } else {
      console.error("Unable to fetch Search service status!", searchResponse.reason)
    }
  }

  return soaasTasks.concat(searchTasks);
}

function UpdateTask(props: {
  task: SomlStatusTask
}) {
  const schemaId = SomlUtils.resolveSchemaId(props.task.id);
  const subOperationName = resolveSubOperationName(props.task);
  const taskName = resolveStatusTaskTitle(props.task.operation, subOperationName, schemaId);
  return <span className="task task-update">{taskName}</span>;
}

const operationToLabelMapping: { [key: string]: (operation: StatusTaskOperation, schemaId: string, subOperation: string) => string } = {
  [StatusTaskOperation.CREATE]: (operation: StatusTaskOperation, schemaId: string, subOperation: string) => `Creating schema ${schemaId} ${subOperation}`,
  [StatusTaskOperation.BIND]: (operation: StatusTaskOperation, schemaId: string, subOperation: string) => `Activating schema ${schemaId} ${subOperation}`,
  [StatusTaskOperation.UPDATE]: (operation: StatusTaskOperation, schemaId: string, subOperation: string) => `Updating schema ${schemaId} ${subOperation}`,
  [StatusTaskOperation.DATA_VALIDATION]: (operation: StatusTaskOperation, schemaId: string, subOperation: string) => `Validating Schema ${schemaId} against Data`,
  [StatusTaskOperation.REMOVE]: (operation: StatusTaskOperation, schemaId: string, subOperation: string) => `Deactivating schema ${schemaId}`,
  [StatusTaskOperation.MANAGE_INDEXES]: (operation: StatusTaskOperation, schemaId: string, subOperation: string) => `${subOperation} ${schemaId}`,
};

function resolveStatusTaskTitle(operation: StatusTaskOperation, subOperation: string, schemaId: string) {
  const mapper = operationToLabelMapping[operation] || UNKNOWN_OPERATION_LABEL_MAPPER;
  return mapper(operation, schemaId, subOperation);
}

function resolveSubOperationName(task: SomlStatusTask) {
  const subTasks = task.subOperations;
  if (subTasks.length === 0) {
    if (task.operation === StatusTaskOperation.MANAGE_INDEXES) {
      return 'Index synchronization for ';
    }
    return '';
  }
  // Response format can have an array of subtasks, but we care only for the first one for now.
  const firstTask = subTasks[0];
  const duration = durationAsString(firstTask.duration);
  let durationStr = " ";
  if (duration) {
    durationStr = ` (${duration}) `;
  }
  switch (firstTask.operation) {
    case StatusTaskOperation.CHECK_REQUIREMENTS:
      return 'Checking indexing requirements for';
    case StatusTaskOperation.PREPARE_ELASTIC:
      return 'Preparing ElasticSearch to index';
    case StatusTaskOperation.CREATE_CONNECTOR:
      return `Indexing ${firstTask.id}${durationStr}for`;
    case StatusTaskOperation.DROP_CONNECTOR:
      return `Cleaning up ${firstTask.id}${durationStr}for`;
    case StatusTaskOperation.CREATE_ALIASES:
      return 'Finalizing indexing of ';
    default:
      return firstTask.operation;
  }
}

function durationAsString(duration: number) {
  if (duration < 1000) {
    // do not display anything if less then a second
    return null;
  }
  const secondsBase = duration / 1000;
  const hours = Math.floor(secondsBase / 3600);
  const minutes = Math.floor((secondsBase - (hours * 3600)) / 60);
  const seconds = Math.floor(secondsBase - (hours * 3600) - (minutes * 60));

  let hourStr;
  let minutesStr;
  const secondsStr = `${seconds}s`;
  if (hours === 0) {
    hourStr = '';
  } else {
    hourStr = `${hours}h `;
  }
  if (minutes === 0) {
    minutesStr = ''
  } else {
    minutesStr = `${minutes}m `
  }
  return `${hourStr} ${minutesStr} ${secondsStr}`;
}
